<template>
  <ModalDialog
    :header-text="currentOption?.title"
    contentClass="cart-modal-content"
    :canBeClosed="!formSubmitting"
    @close-modal="closeModal"
    open
  >
    <template v-slot:content>
      <div v-if="!showApplicationForm" class="cart-modal-content">
        <Stepper
          :value="tabIndex"
          @change="handleTabChange"
          :items="tabs"
          :linear="true"
          data-cy="cart-stepper"
        />
        <div
          v-show="currentTab.type == TabType.Review"
          class="ingredients-review-tab"
          data-cy="ingredients-review-tab"
        >
          <ConfirmationPopup
            v-if="showIngredientRemoveAllConfirmation"
            anchor="ingredients-header-container"
            :text="`Are you sure you want to remove all ingredients?`"
            @closePopup="closeRemoveAllConfirmation"
            data-cy="ingredients-remove-all-confirmation"
          />
          <div
            class="ingredients-header-container"
            ref="ingredients-header-container"
          >
            <span data-cy="total-ingredients-label">
              Total ingredient(s) in cart: {{ ingredients.length }}</span
            >
            <div class="pull-right">
              <a
                class="ingredients-header-container__remove"
                @click="showIngredientRemoveAllConfirmation = true"
                data-cy="ingredients-remove-all-button"
              >
                <font-awesome-icon
                  class="question-icon"
                  icon="fa-solid fa-trash"
                />
                Empty Cart
              </a>
            </div>
          </div>
          <ConfirmationPopup
            v-if="showIngredientRemoveConfirmation"
            anchor="ingredients-container"
            :text="`Are you sure you want to delete ingredient ${ingredientToRemove?.BaseName} ${ingredientToRemove?.BaseNumber} ${ingredientToRemove?.LabelName} ${ingredientToRemove?.LabelNumber}?`"
            @closePopup="closeRemoveConfirmation"
            data-cy="ingredients-remove-confirmation"
          />
          <div v-if="ingredientsLoading" class="ingredients-container">
            <CartItemTileSkeleton
              v-for="n in scheduleStore.getCartCount(type) || 10"
              :key="n"
              :type="type"
            />
          </div>
          <div v-else class="ingredients-container" ref="ingredients-container">
            <IngredientsCartItemTile
              v-for="ingredient in ingredients"
              :key="ingredient.ID"
              :ingredient="ingredient"
              :type="type"
              @editClicked="ingredientEditClicked(ingredient)"
              @removeClicked="ingredientRemoveClicked(ingredient)"
            />
          </div>
          <div class="ingredients-actions">
            <Button
              type="button"
              theme-color="primary"
              @click="changeTab(TabType.Submit)"
              data-cy="ingredients-next-button"
            >
              Next
            </Button>
          </div>
        </div>
        <div
          v-show="currentTab.type == TabType.Submit"
          class="ingredients-submit-tab"
          data-cy="ingredients-submit-tab"
        >
          <form class="ingredients-submit-form" @submit.prevent="onSubmit">
            <div v-if="isSubUser" class="form-control">
              <Checkbox
                id="notify-admin-checkbox"
                v-model="notifyAdmin"
                data-cy="notify-admin-checkbox"
              />
              <label
                class="form-label"
                for="notify-admin-checkbox"
                data-cy="notify-admin-label"
                >CC my administrator at: {{ adminEmail }}</label
              >
            </div>
            <div class="form-control">
              <label
                class="form-label"
                for="submit-email"
                data-cy="submit-email-label"
                >Send additional email to:
              </label>
              <input
                class="form-input form-input--email"
                type="email"
                id="submit-email"
                v-model="email"
                multiple
                data-cy="submit-email-input"
              />
            </div>
            <div class="form-control">
              <Checkbox
                id="save-email-checkbox"
                v-model="saveEmail"
                data-cy="save-email-checkbox"
              />
              <label
                class="form-label"
                for="save-email-checkbox"
                data-cy="save-email-label"
                >SAVE EMAIL ADDRESS</label
              >
            </div>
            <label class="form-control-title" data-cy="comments-label"
              >Comments</label
            >
            <textarea
              class="form-text-area"
              v-model="comments"
              rows="5"
              maxlength="500"
              data-cy="comments-input"
            ></textarea>
            <label
              v-if="holidayMessage"
              class="holiday-label"
              data-cy="holiday-label"
              >{{ holidayMessage }}</label
            >
            <div class="ingredients-actions">
              <Button
                type="button"
                theme-color="secondary"
                @click="changeTab(TabType.Review)"
                data-cy="ingredients-previous-button"
              >
                Previous
              </Button>
              <LoadingButton
                type="submit"
                theme-color="primary"
                :loading="formSubmitting"
                data-cy="ingredients-submit-button"
              >
                Submit
              </LoadingButton>
            </div>
          </form>
        </div>
      </div>
      <div
        v-else
        class="application-form-container"
        data-cy="application-form-container"
      >
        <Button
          class="return-to-cart-button"
          type="button"
          theme-color="secondary"
          :disabled="applicationFormSubmitting"
          @click="showApplicationForm = false"
          data-cy="return-to-cart-button"
        >
          Return to Cart
        </Button>
        <IngredientApplicationForm
          :plants="plants"
          :ingredient="applicationFormIngredient"
          :ownsIds="applicationFormIngredientOwnsIds"
          :showBackAction="true"
          @closeForm="onFormClosed"
          @submittingChanged="(value: boolean) => (applicationFormSubmitting = value)"
        />
      </div>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { SubApplicationType } from "@/Domain/SubApplicationType";
import { useMainStore } from "@/stores/useMainStore";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { computed, onBeforeMount, ref } from "vue";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { Stepper } from "@progress/kendo-vue-layout";
import { useScheduleAStore } from "@/stores/useScheduleAStore";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import IngredientsCartItemTile from "./IngredientsCartItemTile.vue";
import CartItemTileSkeleton from "@/shared/ui/skeletons/CartItemTileSkeleton.vue";
import { Checkbox } from "@progress/kendo-vue-inputs";
import { Button } from "@progress/kendo-vue-buttons";
import ConfirmationPopup from "@/shared/ui/inputs/ConfirmationPopup.vue";
import type { IdAndName } from "@/Domain/IdAndName";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import type { IngredientDisplayInCart } from "@/Domain/IngredientDisplayInCart";
import IngredientApplicationForm from "./IngredientApplicationForm.vue";
import { cartIcon, trackChangesAcceptIcon } from "@progress/kendo-svg-icons";

interface CartOption {
  type: SubApplicationType;
  title: string;
}

enum TabType {
  Review = 0,
  Submit = 1,
}

const main = useMainStore();
const scheduleStore = useScheduleAStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  type: SubApplicationType;
  plants: IdAndName[];
}>();

const emit = defineEmits<{
  (e: "closeModal"): void;
}>();

const ingredients = ref<IngredientDisplayInCart[]>([]);
const ingredientsLoading = ref(false);
const showIngredientRemoveConfirmation = ref(false);
const showIngredientRemoveAllConfirmation = ref(false);
const ingredientToRemove = ref<IngredientDisplayInCart | undefined>(undefined);
const formSubmitting = ref(false);

const applicationFormSubmitting = ref(false);
const applicationFormIngredient = ref<IngredientDisplayInCart>(
  {} as IngredientDisplayInCart
);
const showApplicationForm = ref(false);

const applicationFormIngredientOwnsIds = computed(() => {
  return ingredients.value
    .filter((i) => i.ID === applicationFormIngredient.value.ID)
    .map((i) => Number(i.OwnsIdAsString));
});

const email = ref("");
const adminEmail = ref(main.companySummary!.WebContactEmail);
const saveEmail = ref(false);
const comments = ref("");
const isSubUser = ref(false);
const holidayMessage = ref("");
const notifyAdmin = ref(false);

const options = ref<CartOption[]>([
  {
    type: SubApplicationType.Add,
    title: "Add Ingredient(s)",
  },
  {
    type: SubApplicationType.Terminate,
    title: "Delete Ingredient(s)",
  },
]);

const currentOption = computed<CartOption | undefined>(() => {
  return options.value.find((o) => o.type === props.type);
});

const tabs = [
  {
    type: TabType.Review,
    label: "Review Ingredients",
    svgIcon: cartIcon,
  },
  {
    type: TabType.Submit,
    label: "Submit",
    svgIcon: trackChangesAcceptIcon,
  },
];

const tabIndex = ref(0);

const currentTab = computed(() => {
  return tabs[tabIndex.value];
});

const handleTabChange = function (e: any) {
  tabIndex.value = e.value;
};

const ingredientEditClicked = function (ingredient: IngredientDisplayInCart) {
  applicationFormIngredient.value = ingredient;
  console.debug(
    "applicationFormIngredientOwnsIds",
    applicationFormIngredientOwnsIds.value
  );
  showApplicationForm.value = true;
};

const ingredientRemoveClicked = function (ingredient: IngredientDisplayInCart) {
  ingredientToRemove.value = ingredient;
  showIngredientRemoveConfirmation.value = true;
};

const closeRemoveConfirmation = function (confirmed: boolean) {
  showIngredientRemoveConfirmation.value = false;
  if (confirmed && ingredientToRemove.value) {
    deleteIngredientFromCartAsync(ingredientToRemove.value);
  }
};

const closeRemoveAllConfirmation = function (confirmed: boolean) {
  showIngredientRemoveAllConfirmation.value = false;
  if (confirmed) {
    deleteAllIngredientsFromCartAsync();
  }
};

const changeTab = function (tabType: TabType) {
  const index = tabs.findIndex((t) => t.type === tabType);
  if (index === -1) {
    console.error(
      `IngredientsCartModal.vue, changeTab(), tab with type ${tabType} not found`
    );
    return;
  }
  tabIndex.value = index;
};

const deleteIngredientFromCartAsync = async function (
  ingredient: IngredientDisplayInCart
) {
  const requestPromise = api.get("VueDeleteIngredientFromCart", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    subApplication:
      props.type != SubApplicationType.Terminate ? props.type : "delete",
    ingredientId: ingredient.ID.toString(),
    ingredientKey: ingredient.Key.toString(),
    ownsId: ingredient.OwnsId.toString(),
  });

  await handleResponsePromiseAsync(
    requestPromise,
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.success,
        message: "Ingredient removed from cart",
        duration: 5000,
      });
      updateCartIngredientsAsync();
    },
    "IngredientsCartModal.vue",
    "deleteIngredientFromCartAsync",
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: "Error removing ingredient from cart. Please try again.",
        duration: 5000,
      });
    }
  );
};

const deleteAllIngredientsFromCartAsync = async function () {
  return handleResponsePromiseAsync(
    api.get("DeleteAllIngredientsFromCart", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      subApplication:
        props.type != SubApplicationType.Terminate ? props.type : "delete",
    }),
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.success,
        message: "All ingredients removed from cart",
        duration: 5000,
      });
      scheduleStore.setCartCount(props.type, 0);
      closeModal(true);
    },
    "IngredientsCartModal.vue",
    "deleteAllIngredientsFromCartAsync",
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: "Error removing ingredients from cart. Please try again.",
        duration: 5000,
      });
    }
  );
};

const updateCartIngredientsAsync = async function () {
  ingredientsLoading.value = true;

  const cartPromise = api.get(
    "VueGetAllMatchingIngredientsIncartSubApplicationAndUserLogin",
    {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      subApplication:
        props.type != SubApplicationType.Terminate
          ? props.type.toString()
          : "delete",
    }
  );

  await handleResponsePromiseAsync(
    cartPromise,
    (data) => {
      ingredients.value = data;
      scheduleStore.setCartCount(props.type, data.length);
    },
    "IngredientsCartModal.vue",
    "updateCartIngredientsAsync"
  );

  ingredientsLoading.value = false;
};

const updateUserIngredientCartEmailAsync = async function () {
  const emailPromise = api.get("GetPropertyValues", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    propertyName: "ingredientCartEmail",
  });

  return handleResponsePromiseAsync(
    emailPromise,
    (data) => {
      if (data && data[0]) email.value = data[0];
    },
    "IngredientsCartModal.vue",
    "updateUserEmailAsync"
  );
};

const isSubUserAsync = async function () {
  return api.get("IsSubUser", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    userName: main.userLogin,
  });
};

const updateIsSubUserAsync = async function () {
  return handleResponsePromiseAsync(
    isSubUserAsync(),
    (data) => {
      isSubUser.value = data;
      notifyAdmin.value = true;
    },
    "IngredientsCartModal.vue",
    "updateIsSubUserAsync"
  );
};

const updateHolidayMessageAsync = async function () {
  return handleResponsePromiseAsync(
    api.get("GetHolidayMessage", {}),
    (data) => {
      holidayMessage.value = data;
    },
    "IngredientsCartModal.vue",
    "updateHolidayMessageAsync"
  );
};

const getUserEmailAsync = async function () {
  return api.get("GetUserEmail", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    userName: main.userLogin,
    userIsOnline: true,
  });
};

const generateUserEmailAddressForSubmissionAsync = async function () {
  let emailListString = "";
  if (notifyAdmin.value) {
    emailListString = `${email.value?.replaceAll(",", ";")};${
      adminEmail.value
    }`;
  } else {
    emailListString = email.value?.replaceAll(",", ";");
  }

  let userEmail = "";

  await handleResponsePromiseAsync(
    getUserEmailAsync(),
    (data) => {
      userEmail = data;
    },
    "IngredientsCartModal.vue",
    "generateUserEmailAddressForSubmissionAsync"
  );

  emailListString = `${userEmail};${emailListString}`;

  if (main.isEmulator)
    emailListString = `${userEmail};${emailListString};${adminEmail.value}`;
  else emailListString = `${userEmail};${emailListString}`;

  return emailListString;
};

const saveUserCartEmailAsync = async function () {
  return api.get("SetPropertyValues", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    propertyName: "ingredientCartEmail",
    propertyValue: email.value,
  });
};

const submitIngredientsCartAsync = async function (emailListString: string) {
  return api.get("VueSubmitIngredientsCart", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    subApplication:
      props.type != SubApplicationType.Terminate
        ? props.type.toString()
        : "delete",
    userEmail: emailListString,
    realUserLogin: main.userLogin,
    comments: comments.value,
  });
};

const onSubmit = async function () {
  if (formSubmitting.value) return;
  formSubmitting.value = true;

  if (saveEmail.value) await saveUserCartEmailAsync();

  const emailListString = await generateUserEmailAddressForSubmissionAsync();

  const errorNotification = {
    type: NotificationTypeEnum.error,
    message: "There was an error submitting your request. Please try again.",
    duration: 5000,
  };

  await handleResponsePromiseAsync(
    submitIngredientsCartAsync(emailListString),
    (data: string) => {
      if (data && data != "0") {
        notificationStore.notify({
          type: NotificationTypeEnum.success,
          message: `Your request has been submitted. Click Submissions menu option to review all previous submissions`,
          duration: 5000,
        });
        scheduleStore.setCartCount(props.type, 0);
        closeModal(true);
      } else {
        notificationStore.notify(errorNotification);
      }
    },
    "IngredientsCartModal.vue",
    "onSubmit",
    () => {
      notificationStore.notify(errorNotification);
    }
  );
  formSubmitting.value = false;
};

const onFormClosed = function (submitted: boolean) {
  showApplicationForm.value = false;
  if (submitted) updateCartIngredientsAsync();
};

const closeModal = function (closed: boolean) {
  if (!closed) {
    notificationStore.notify({
      type: NotificationTypeEnum.info,
      message: "Please wait while ingredients are being processed",
      duration: 5000,
    });
    return;
  }
  emit("closeModal");
};

onBeforeMount(() => {
  updateUserIngredientCartEmailAsync();
  updateIsSubUserAsync();
  updateHolidayMessageAsync();
  updateCartIngredientsAsync();
});
</script>

<style lang="scss">
.cart-modal-content {
  max-width: 600px;
  width: 600px;

  @media (max-width: 720px) {
    max-width: 100%;
    width: 100%;
  }

  .ingredients-header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    font-size: 14px;
    font-weight: 600;
    color: #333333;
    padding: 0 10px;
    margin-top: 36px;
    gap: 48px;

    &__remove {
      display: flex;
      align-items: center;
      justify-content: center;
      color: #333333;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;

      .question-icon {
        margin-right: 5px;
      }
    }
  }

  .ingredients-review-tab {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 160px);

    @include media-breakpoint-up(sm) {
      display: block;
      height: auto;
    }
  }

  .ingredients-container {
    overflow-y: auto;

    @include media-breakpoint-up(sm) {
      max-height: 360px;
    }
  }

  .ingredients-submit-form {
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;
    padding: 0 10px;
    margin-top: 36px;
    gap: 16px;

    .form-control {
      display: flex;
      align-items: center;
      gap: 10px;

      &-title {
        margin-top: 16px;
      }

      .form-input--email {
        width: 50%;
      }
    }
  }

  .ingredients-actions {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }
}

.application-form-container {
  max-width: 600px;
  padding: 2rem;

  .return-to-cart-button {
    margin-bottom: 24px;
  }
}
</style>
